import { Entity, EntityResolved } from '@toggle/toggle';
import { useCallback, useState } from 'react';

import {
  FormattedResolvedEntityV3Data,
  resolveEntitiesFromCsvV3,
} from '~/stores/use-watchlist/entityResolver';
import {
  isNewEntity,
  mergeDuplicateTickers,
  mergeUnMappedEntitiesLists,
  splitAllResolvedEntities,
} from '~/views/onboarding/utils/utils';

interface UseEntityResolutionProps {
  entitiesList: EntityResolved[];
  unMappedEntitiesList: { input: string }[];
  setResolvedEntityData: (data: FormattedResolvedEntityV3Data) => void;
  setUnMappedEntitiesList: (list: { input: string }[]) => void;
  setAllDuplicateTickers: (duplicates: string[]) => void;
  setWatchlistError: (error: undefined) => void;
  updateEntitiesList: (params: {
    entitiesList: EntityResolved[];
    newEntities?: EntityResolved[];
  }) => void;
  entityAddedClear: (count: number) => void;
}

export const useEntityResolution = ({
  entitiesList,
  unMappedEntitiesList,
  setResolvedEntityData,
  setUnMappedEntitiesList,
  setAllDuplicateTickers,
  setWatchlistError,
  updateEntitiesList,
  entityAddedClear,
}: UseEntityResolutionProps) => {
  const [isPending, setIsPending] = useState(false);

  const handleEntities = useCallback(
    (result: FormattedResolvedEntityV3Data) => {
      setResolvedEntityData(result);

      const {
        mappedEntities,
        unMappedEntitiesList: searchUnMappedEntities,
        tableDuplicates,
      } = splitAllResolvedEntities(
        result.entities.map(e => e.primaryEntity),
        entitiesList
      );

      const updatedUnmappedEntities = mergeUnMappedEntitiesLists(
        unMappedEntitiesList,
        searchUnMappedEntities
      );
      setUnMappedEntitiesList(updatedUnmappedEntities);

      const duplicates = mergeDuplicateTickers(
        result.duplicates,
        tableDuplicates
      );
      setAllDuplicateTickers(duplicates);

      const newEntities = mappedEntities.filter(r =>
        isNewEntity({ tag: r.tag, entitiesList })
      ) as EntityResolved[];

      updateEntitiesList({ newEntities, entitiesList });
      entityAddedClear(newEntities.length);
    },
    [
      entitiesList,
      unMappedEntitiesList,
      setResolvedEntityData,
      setUnMappedEntitiesList,
      setAllDuplicateTickers,
      updateEntitiesList,
      entityAddedClear,
    ]
  );

  const onChatSubmit = useCallback(
    async (value: string) => {
      setIsPending(true);
      setWatchlistError(undefined);
      const result = await resolveEntitiesFromCsvV3(value);
      handleEntities(result);
      setIsPending(false);
    },
    [setWatchlistError, handleEntities]
  );

  const removeEntity = useCallback(
    (entity: EntityResolved) => {
      updateEntitiesList({
        entitiesList: entitiesList.filter(d => d.tag !== entity.tag),
      });
    },
    [entitiesList, updateEntitiesList]
  );

  const onSelectItem = useCallback(
    (entity: Entity, isInWatchlist: boolean) => {
      const entityResolved = {
        ...entity,
        input: '',
      };

      setWatchlistError(undefined);

      if (isInWatchlist) {
        removeEntity(entityResolved);
      } else {
        updateEntitiesList({ newEntities: [entityResolved], entitiesList });
        entityAddedClear(1);
      }
    },
    [
      entitiesList,
      setWatchlistError,
      removeEntity,
      updateEntitiesList,
      entityAddedClear,
    ]
  );

  const getIsInWatchlist = useCallback(
    (item: Entity) => entitiesList.some(d => d.tag === item.tag),
    [entitiesList]
  );

  return {
    isPending,
    handleEntities,
    onChatSubmit,
    onSelectItem,
    removeEntity,
    getIsInWatchlist,
  };
};
